import { FileModel, Page } from "../../../shared/models/models";
import {
  CategoryReference,
  InstalledStockItem,
  InstalledStockItemsQuery, InternalStockItem,
  InternalStockItemsQuery
} from "../../inventory/models/models";

export interface CustomerMachineryState {
  query: MachinesQuery;
  machines: Record<string, Machine[]>;
  machine?: Machine;
  manufacturers: ManufacturerReference[];
  operationTypes: OperationType[];
  axisConfigurations: AxisConfiguration[];
  controlSystemBrands: ControlSystemBrand[];
  controlSystemModels: ControlSystemModel[];
  automationLevels: AutomationLevel[];
  types: MachineType[];
  loading: boolean;
  success?: string;
  error: any;
  // Decommission state
  decommissionedMachines: Machine[];
  decommissionedLoading: boolean;
  decommissionPreview: DecommissionPreview | null;
  decommissionPreviewLoading: boolean;
}

export interface CustomerMachineState {
  installedStockItemsQuery: InstalledStockItemsQuery;
  machine?: Machine;
  installedStockItems?: InstalledStockItem[];
  categories: CategoryReference[];
  manufacturers: ManufacturerReference[];
  loading: boolean;
  installedStockItemsLoading: boolean;
  success?: string;
  error: any;
}

export interface CustomerInternalStockState {
  internalStockItemsQuery: InternalStockItemsQuery;
  internalStockItems?: Page<InternalStockItem>;
  categories: CategoryReference[];
  manufacturers: ManufacturerReference[];
  loading: boolean;
  success?: string;
  error: any;
}

export interface TaperType {
  id: string;
  name: string;
  standard: string;
  description?: string;
  sortOrder?: number;
}

export interface MachineFileItem {
  id?: string;
  name: string;
  content: string; // base64 or blob URL
  displayOrder: number;
  isPrimary: boolean;
  size?: number;
}

export interface Machine {
  id: string;
  customerId: string;
  model: string;
  serialNumber: string;
  internalNumber: string;
  system: string;
  version: string;
  productionYear?: number;
  description: string;
  openServiceReportsCount: number;
  serviceStatusLabel?: string;
  serviceStatusColor?: string;
  serviceRequestStatus?: string;
  serviceRequestStatusColor?: string;
  activeServiceRequestCount?: number;
  statusDate: string;
  type: MachineType;
  manufacturer: ManufacturerReference;
  customBrandName: string;
  operationType: OperationType;
  axisConfiguration: AxisConfiguration;
  controlSystemBrand: ControlSystemBrand;
  numberOfStations?: number;
  automationLevel: AutomationLevel;
  catalogModelId?: string;
  catalogModel?: CatalogModelListItem;
  catalogImageUrl?: string;
  image: FileModel | null;
  files?: MachineFileItem[];
  botActivated: boolean;
  groupNames?: string[];
  isDecommissioned?: boolean;
  decommissionedDate?: string;
  decommissionReason?: string;
  // Phase 7F: TaperType + key specs from catalog model
  taperType?: TaperType;
  maxSpindleRpm?: number;
  spindleHp?: number;
  spindleDirection?: string;
  toolCapacity?: number;
  tableSizeMm?: string;
  maxWorkpieceKg?: number;
  // LOC-1: Location fields (denormalized from LocationReference)
  locationId?: string;
  locationName?: string;
  parentLocationName?: string;
}

export interface PublicMachineProfile {
  id: string;
  model: string;
  customBrandName?: string;
  manufacturerName?: string;
  typeName?: string;
  serialNumber: string;
  productionYear?: number;
  operationTypeName?: string;
  controlSystemBrandName?: string;
  system?: string;
  axisConfigurationName?: string;
  automationLevelName?: string;
  image?: FileModel;
  catalogModelId?: string;
  customerName?: string;
  customerId: string;
  groupNames: string[];
  isOwner: boolean;
}

export interface ChangeMachine {
  model: string;
  serialNumber: string;
  typeId?: string;
  manufacturerId?: string;
  customBrandName?: string;
  operationTypeId?: string;
  axisConfigurationId?: string;
  controlSystemBrandId?: string;
  automationLevelId?: string;
  system?: string;
  version?: string;
  productionYear?: number;
  description?: string;
  numberOfStations?: number;
  catalogModelId?: string;
  image: FileModel | null;
  files?: MachineFileItem[];
  locationId?: string;
}

export interface MachineType {
  id: string;
  name: string;
}

export interface OperationType {
  id: string;
  name: string;
}

export interface AxisConfiguration {
  id: string;
  name: string;
}

export interface ControlSystemBrand {
  id: string;
  name: string;
}

export interface ControlSystemModel {
  id: string;
  name: string;
}

export interface ManufacturerReference {
  id: string;
  name: string;
}

export interface AutomationLevel {
  id: string;
  name: string;
}

export interface MachinesQuery {
  search?: string;
  manufacturerId?: string;
  typeId?: string;
  groupBy?: string;
}

export const MachineTypes = {
    cnc: "CNC Machine",
    productionLine: "Production line",
    other: "Other",
};

// --- Catalog models ---

export interface CatalogManufacturerSummary {
  id: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  manufacturerId: string | null;
  parentManufacturerId: string | null;
  parentManufacturerName: string | null;
}

export interface CatalogModelListItem {
  id: string;
  techspexId: number;
  modelName: string;
  categoryCode: string;
  subType: string;
  cncBrand: string;
  numAxes: number | null;
  imageUrl: string | null;
  imageStandardUrl: string | null;
  imageDetailUrl: string | null;
  specsCount: number;
  manufacturer: CatalogManufacturerSummary;
  // Phase 7F: Classification data
  operationTypeName?: string;
  axisConfigurationName?: string;
  controlSystemBrandName?: string;
  taperTypeName?: string;
  // Phase 7F: Key specs
  maxSpindleRpm?: number;
  spindleHp?: number;
  spindleDirection?: string;
  toolCapacity?: number;
  tableSizeMm?: string;
  // Catalog model images (angle/detail photos)
  images?: CatalogModelImage[];
}

export interface CatalogSpecItem {
  specKey: string;
  specValue: string;
  numericValue: number | null;
  unit: string;
}

export interface CatalogSpecSection {
  sectionName: string;
  specs: CatalogSpecItem[];
}

export interface CatalogModelImage {
  id: string;
  imageStandardUrl: string;
  imageDetailUrl: string;
  imageType: string;
  displayOrder: number;
  isPrimary: boolean;
}

export interface CatalogModelDetail {
  id: string;
  techspexId: number;
  modelName: string;
  categoryCode: string;
  subType: string;
  operationType: string;
  cncBrand: string;
  cncModel: string;
  numAxes: number | null;
  numAxesOpt: number | null;
  imageUrl: string | null;
  imagePath: string | null;
  imageStandardUrl: string | null;
  imageDetailUrl: string | null;
  sourceUrl: string;
  isActive: boolean;
  specsCount: number;
  manufacturer: CatalogManufacturerSummary;
  specSections: CatalogSpecSection[];
  images?: CatalogModelImage[];
}

export interface CatalogSearchResult {
  items: CatalogModelListItem[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface CatalogManufacturer {
  id: string;
  techspexId: number;
  name: string;
  slug: string;
  logoUrl: string | null;
  machineCount: number;
  manufacturerId: string | null;
  parentManufacturerId: string | null;
  parentManufacturerName: string | null;
  childManufacturers: CatalogManufacturerSummary[];
}

export interface CatalogCategory {
  id: number;
  code: string;
  name: string;
  modelCount: number;
}

export interface CatalogBrowseQuery {
  search?: string;
  categoryCode?: string;
  manufacturerId?: string;
  page: number;
  pageSize: number;
  // Phase 7F: Spec-based filters
  minRpm?: number;
  maxRpm?: number;
  minHp?: number;
  maxHp?: number;
  minTools?: number;
  maxTools?: number;
  taperTypeId?: string;
  controlSystemBrandId?: string;
  operationTypeId?: string;
}

export interface MachineCatalogState {
  // Dialog search state
  searchResults: CatalogModelListItem[];
  searchTotalCount: number;
  searchLoading: boolean;
  selectedModel: CatalogModelDetail | null;
  selectedModelLoading: boolean;
  manufacturers: CatalogManufacturer[];
  categories: CatalogCategory[];
  taperTypes: TaperType[];

  // Browse page state
  browseResults: CatalogModelListItem[];
  browseTotalCount: number;
  browseTotalPages: number;
  browseLoading: boolean;
}

// --- Decommission models ---

export interface ServiceRequestBrief {
  id: string;
  internalNumber: string;
  issue: string;
  statusCode: number;
}

export interface RfsSummary {
  draftCount: number;
  openNoOffersCount: number;
  negotiatingCount: number;
  inProgressCount: number;
  totalActiveCount: number;
  hasBlockingRequests: boolean;
  blockingRequests: ServiceRequestBrief[];
}

export interface DecommissionPreview {
  machineId: string;
  machineName: string;
  machineImage: string;
  rfsSummary: RfsSummary;
  canDecommission: boolean;
  blockReason: string;
}

// --- Report Problem models ---

export interface ReportProblemRequest {
  description: string;
  priorityCode?: string;
  photos?: string[];
}

export interface ReportProblemResponse {
  serviceRequestId: string;
  serviceReportNumber: string;
  message: string;
}